import { buildAuthHeaders, getClientBaseUrl } from "@client/trpc";

/**
 * Download a round-scoped file from an Express API endpoint with the same
 * viewer auth every other request sends: the session cookie on web, the
 * bearer token on native builds, and — when an SFC admin is impersonating —
 * the `x-acting-as-user-id` header, so the server scopes the download to the
 * acted-as user exactly like the rest of the app.
 *
 * Plain `<a href>` / `window.location.href` navigation can carry none of
 * those headers, which silently downloads as the underlying user during
 * impersonation. Round-scoped downloads must go through here (or attach
 * `buildAuthHeaders()` themselves) rather than navigating.
 */
export async function downloadRoundScopedFile({
  path,
  filename,
}: {
  /** Endpoint path starting with /api/. */
  path: string;
  /** Name the browser saves the file as. */
  filename: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  // The whole flow lives in one try: consuming the body (`blob()`) can reject
  // just like `fetch()` when the connection drops mid-download, and callers
  // rely on the returned union — this function must never throw.
  try {
    const response = await fetch(`${getClientBaseUrl()}${path}`, {
      method: "GET",
      // Same cookie semantics as the tRPC client and the round-aggregate
      // fetch: same-origin cookie on web; native rides the bearer header.
      credentials: "same-origin",
      headers: buildAuthHeaders(),
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: string;
      } | null;
      return {
        ok: false,
        error: body?.error ?? `Download failed (${response.status})`,
      };
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch {
    return {
      ok: false,
      error: "Download failed — check your network connection",
    };
  }
}
